import React, {
    useState,
    useEffect,
    useLayoutEffect,
    useMemo,
    useCallback,
    useRef,
} from 'react';
import { MdFullscreenExit } from 'react-icons/md';
import { useSelector } from 'react-redux';

import { Button, Tooltip, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';

import api from '~/services/pluginbot-api';
import getDateLocale from '~/util/GetDateLocale';
import GetPluginspaceTheme from '~/util/PluginspaceTheme';

import { Container } from '../../Dashboard/styles';
import { GENERIC_DETAIL_ERROR } from '../CleaningReportsDashboard/cleaningReportDetailConstants';
import CleaningReportDetailModal from '../CleaningReportsDashboard/CleaningReportDetailModal';
import { buildCleaningReportShortcutList } from '../CleaningReportsDashboard/cleaningReportShortcutList';
import { CLEANING_REPORT_TASKS_TABLE_DEFAULT_ROWS_PER_PAGE } from '../CleaningReportsDashboard/CleaningReportTasksTablePagination';
import {
    buildCleanAreaByHourOfDay,
    buildCleanAreaVsTaskAreaByDay,
    buildCleaningReportApiTimeRange,
    cleaningDetailErrorMessageFromPayload,
    cleaningReportDefaultShortcut,
    defaultCleaningReportDateRange,
    formatCleaningDurationNoSeconds,
    getCleaningReportChartColors,
    localeToBcp47,
    normalizeAggregatedCleaningReportResponse,
    scrollCleaningReportTableIntoView,
    summarizeList,
} from '../CleaningReportsDashboard/cleaningReportUtils';
import {
    useCleaningReportCurrShortcutSync,
    useCleaningReportPeriodHandlers,
} from '../CleaningReportsDashboard/useCleaningReportPeriod';
import CleaningReportPluginspaceFullscreen from './CleaningReportPluginspaceFullscreen';
import CleaningReportPluginspaceLiveShell from './CleaningReportPluginspaceLiveShell';
import CleaningReportPluginspaceMainCard from './CleaningReportPluginspaceMainCard';
import CleaningReportPluginspaceRobotErrorsAlert from './CleaningReportPluginspaceRobotErrorsAlert';
import {
    CC1_ROBOT_FILTER_SELECT_ALL_VALUE,
    CLEANING_REPORT_DATA_FS_BODY_CLASS,
    LIVE_REFRESH_MS,
    ROBOT_TYPE_SLUG_CC1,
} from './constants';
import useCleaningReportPluginspaceStyles from './styles';

export default function CleaningReportPluginspace() {
    const classes = useCleaningReportPluginspaceStyles();
    const muiTheme = useTheme();
    const colors = GetPluginspaceTheme(muiTheme);

    const [reportLoading, setReportLoading] = useState(true);
    const [initialDone, setInitialDone] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [fleetSettings, setFleetSettings] = useState({
        human_productivity: 0,
        discharge_rate: 20,
        tank_capacity: 3,
        manual_reference: 500,
        chemical_dilution_ratio: 10,
    });

    const [reportPayload, setReportPayload] = useState(() =>
        normalizeAggregatedCleaningReportResponse(null)
    );
    const [periodDates, setPeriodDates] = useState(
        defaultCleaningReportDateRange
    );
    const [draftPeriod, setDraftPeriod] = useState(
        defaultCleaningReportDateRange
    );
    const [currShortcut, setCurrShortcut] = useState(() => {
        const d = defaultCleaningReportDateRange();
        return cleaningReportDefaultShortcut(d.startDate, d.endDate);
    });

    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [dataFullscreen, setDataFullscreen] = useState(false);
    const cleaningTableRef = useRef(null);

    const [tasksTablePage, setTasksTablePage] = useState(0);
    const [tasksTableRowsPerPage, setTasksTableRowsPerPage] = useState(
        CLEANING_REPORT_TASKS_TABLE_DEFAULT_ROWS_PER_PAGE
    );

    const [cc1Robots, setCc1Robots] = useState([]);
    const [cc1RobotsLoading, setCc1RobotsLoading] = useState(true);
    const [selectedCc1RobotIds, setSelectedCc1RobotIds] = useState([]);
    const [draftSelectedCc1RobotIds, setDraftSelectedCc1RobotIds] = useState(
        []
    );

    const reduxSettings = useSelector(state => state.settings || {});
    const localeCode =
        (reduxSettings.locale && reduxSettings.locale.code) || 'pt_BR';
    const activeGroupId = reduxSettings.active && reduxSettings.active.id;
    const date_loc = getDateLocale(reduxSettings);

    const prevActiveGroupIdRef = useRef();
    useEffect(() => {
        const prev = prevActiveGroupIdRef.current;
        if (prev !== undefined && prev !== activeGroupId) {
            window.location.reload();
            return;
        }
        prevActiveGroupIdRef.current = activeGroupId;
    }, [activeGroupId]);

    const shortcut_list = buildCleaningReportShortcutList();

    const {
        list,
        total,
        robots_queried: robotsQueried,
        robots_errors,
    } = reportPayload;

    const areaCompareSeries = useMemo(
        () =>
            buildCleanAreaVsTaskAreaByDay(
                list,
                localeToBcp47(localeCode),
                periodDates
            ),
        [list, localeCode, periodDates]
    );
    const areaByHourSeries = useMemo(() => buildCleanAreaByHourOfDay(list), [
        list,
    ]);
    const summary = useMemo(() => summarizeList(list), [list]);
    const totalCleanFormatted = formatCleaningDurationNoSeconds(
        summary.totalCleanTimeSec
    );
    const totalArea = summary.totalCleanArea.toFixed(1);

    const [fsAreaChartHeight, setFsAreaChartHeight] = useState(210);

    const tableRowsForDisplay = useMemo(() => {
        const start = tasksTablePage * tasksTableRowsPerPage;
        return list.slice(start, start + tasksTableRowsPerPage);
    }, [list, tasksTablePage, tasksTableRowsPerPage]);

    const selectedCc1RobotIdsKey = useMemo(
        () =>
            [...selectedCc1RobotIds]
                .map(String)
                .sort()
                .join(','),
        [selectedCc1RobotIds]
    );

    useEffect(() => {
        setTasksTablePage(0);
    }, [periodDates.startDate, periodDates.endDate, selectedCc1RobotIdsKey]);

    useEffect(() => {
        const totalPages = Math.max(
            1,
            Math.ceil(list.length / tasksTableRowsPerPage)
        );
        const maxPage = totalPages - 1;
        if (tasksTablePage > maxPage) {
            setTasksTablePage(Math.max(0, maxPage));
        }
    }, [list.length, tasksTableRowsPerPage, tasksTablePage]);

    const handleTasksTablePageChange = useCallback((event, newPage) => {
        setTasksTablePage(newPage);
    }, []);

    const handleTasksTableRowsPerPageChange = useCallback(event => {
        setTasksTableRowsPerPage(parseInt(event.target.value, 10));
        setTasksTablePage(0);
    }, []);

    useEffect(() => {
        let cancelled = false;
        setCc1RobotsLoading(true);
        api.get('robots/filter', {
            params: { type_slug: ROBOT_TYPE_SLUG_CC1 },
        })
            .then(res => {
                if (cancelled) return;
                const robots = Array.isArray(res.data) ? res.data : [];
                setCc1Robots(robots);
                const initialIds = robots.map(r => r && r.id).filter(Boolean);
                setSelectedCc1RobotIds(initialIds);
                setDraftSelectedCc1RobotIds(initialIds);
            })
            .catch(() => {
                if (!cancelled) {
                    setCc1Robots([]);
                    setSelectedCc1RobotIds([]);
                    setDraftSelectedCc1RobotIds([]);
                }
            })
            .finally(() => {
                if (!cancelled) setCc1RobotsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (selectedCc1RobotIds.length === 0) {
            setFleetSettings({
                human_productivity: 0,
                discharge_rate: 20,
                tank_capacity: 3,
                manual_reference: 500,
                chemical_dilution_ratio: 10,
            });
            return () => {
                cancelled = true;
            };
        }

        async function fetchFleetSettings() {
            try {
                // 1. Fetch all selected robots to get their application IDs
                const robotPromises = selectedCc1RobotIds.map(rid =>
                    api.get(`robots/${rid}`)
                );
                const robotResponses = await Promise.all(robotPromises);
                if (cancelled) return;

                const appIds = robotResponses
                    .map(res => {
                        const robotData = res.data;
                        return (
                            robotData &&
                            robotData.settings &&
                            robotData.settings.applications &&
                            robotData.settings.applications.cleaning
                        );
                    })
                    .filter(Boolean);

                // 2. Fetch all unique applications
                const uniqueAppIds = [...new Set(appIds)];
                const appPromises = uniqueAppIds.map(aid =>
                    api.get(`applications/${aid}`)
                );
                const appResponses = await Promise.all(appPromises);
                if (cancelled) return;

                const appMap = {};
                appResponses.forEach(res => {
                    if (res.data && res.data.id) {
                        const app = res.data;
                        const settings =
                            app.settings && app.settings.settings
                                ? app.settings.settings
                                : app.settings || {};
                        appMap[app.id] = settings;
                    }
                });

                // 3. Aggregate settings
                // We use the full selectedCc1RobotIds list to account for each robot's assigned app
                const aggregated = {
                    human_productivity: 0,
                    discharge_rate: 0,
                    tank_capacity: 0,
                    manual_reference: 0,
                    chemical_dilution_ratio: 0,
                    count: 0,
                };

                selectedCc1RobotIds.forEach(rid => {
                    const robotRes = robotResponses.find(
                        r => r.data.id === rid
                    );
                    const appId =
                        robotRes &&
                        robotRes.data.settings &&
                        robotRes.data.settings.applications &&
                        robotRes.data.settings.applications.cleaning;

                    // If no application is assigned, ignore this robot for cleaning metrics
                    if (!appId || !appMap[appId]) {
                        return;
                    }

                    const settings = appMap[appId];

                    aggregated.human_productivity +=
                        parseFloat(settings.human_productivity) || 150;
                    aggregated.tank_capacity +=
                        parseFloat(settings.tank_capacity) || 3;

                    // For rates and ratios, we'll average them
                    aggregated.discharge_rate +=
                        parseFloat(settings.discharge_rate) || 20;
                    aggregated.manual_reference +=
                        parseFloat(settings.manual_reference) || 500;
                    aggregated.chemical_dilution_ratio +=
                        parseFloat(settings.chemical_dilution_ratio) || 10;
                    aggregated.count += 1;
                });

                if (aggregated.count > 0) {
                    setFleetSettings({
                        human_productivity: aggregated.human_productivity,
                        tank_capacity: aggregated.tank_capacity,
                        discharge_rate:
                            aggregated.discharge_rate / aggregated.count,
                        manual_reference:
                            aggregated.manual_reference / aggregated.count,
                        chemical_dilution_ratio:
                            aggregated.chemical_dilution_ratio /
                            aggregated.count,
                    });
                }
            } catch (err) {
                if (!cancelled) {
                    // Fallback to defaults
                    setFleetSettings({
                        human_productivity: selectedCc1RobotIds.length * 150,
                        discharge_rate: 20,
                        tank_capacity: selectedCc1RobotIds.length * 3,
                        manual_reference: 500,
                        chemical_dilution_ratio: 10,
                    });
                }
            }
        }

        fetchFleetSettings();

        return () => {
            cancelled = true;
        };
    }, [selectedCc1RobotIds]);

    const handleCc1RobotFilterChange = useCallback(
        e => {
            const val = e.target.value;
            const allIds = cc1Robots.map(r => r && r.id).filter(Boolean);
            const allSelectedLocal =
                allIds.length > 0 &&
                allIds.every(id => draftSelectedCc1RobotIds.includes(id));

            if (val.includes(CC1_ROBOT_FILTER_SELECT_ALL_VALUE)) {
                setDraftSelectedCc1RobotIds(
                    allSelectedLocal ? [] : [...allIds]
                );
                return;
            }
            setDraftSelectedCc1RobotIds(val);
        },
        [cc1Robots, draftSelectedCc1RobotIds]
    );

    const handleCc1RobotFilterConfirm = useCallback(() => {
        const prevSorted = [...selectedCc1RobotIds].sort().join(',');
        const nextSorted = [...draftSelectedCc1RobotIds].sort().join(',');
        if (prevSorted !== nextSorted) {
            setSelectedCc1RobotIds(draftSelectedCc1RobotIds);
        }
    }, [selectedCc1RobotIds, draftSelectedCc1RobotIds]);

    useLayoutEffect(() => {
        if (!dataFullscreen) return undefined;
        const run = () => {
            const vh = window.innerHeight;
            const errH =
                (loadError || reportPayload.apiError ? 20 : 0) +
                (robots_errors && robots_errors.length ? 20 : 0);
            const tableBlock = Math.min(210, Math.round(vh * 0.24));
            const kpiH = 72;
            const padding = 52 + 10;
            const chartChrome = 50;
            const gridGaps = 48;
            const avail =
                vh -
                padding -
                kpiH -
                tableBlock -
                errH -
                chartChrome -
                gridGaps;
            setFsAreaChartHeight(
                Math.max(148, Math.min(275, Math.round(avail)))
            );
        };
        run();
        window.addEventListener('resize', run);
        return () => window.removeEventListener('resize', run);
    }, [dataFullscreen, loadError, reportPayload.apiError, robots_errors]);

    const fetchAggregatedReportForRange = useCallback(
        (startYmd, endYmd, options = {}) => {
            const { silent = false, forceRefresh = false } = options;
            const { start_time, end_time } = buildCleaningReportApiTimeRange(
                startYmd,
                endYmd
            );
            const allIds = cc1Robots.map(r => r && r.id).filter(Boolean);
            const params = { start_time, end_time };
            if (selectedCc1RobotIds.length === 0) {
                params.robot_uids = '';
            } else if (
                allIds.length > 0 &&
                selectedCc1RobotIds.length === allIds.length &&
                allIds.every(id => selectedCc1RobotIds.includes(id))
            ) {
                /* todos selecionados — omitir robot_uids (comportamento legado da API) */
            } else {
                params.robot_uids = selectedCc1RobotIds.join(',');
            }
            if (forceRefresh) {
                params.refresh = 'true';
            }
            if (!silent) {
                setReportLoading(true);
            }
            setLoadError(null);
            return api
                .get('robots/cleaning-report/cc1', {
                    params: {
                        ...params,
                        ...(options.include_details
                            ? { include_details: true }
                            : {}),
                    },
                })
                .then(res => {
                    setReportPayload(prev => {
                        const next = normalizeAggregatedCleaningReportResponse(
                            res.data
                        );
                        // Se for uma carga enriquecida, preservar a lista original mas atualizar os campos de custo
                        if (options.include_details) {
                            return {
                                ...prev,
                                list: prev.list.map(oldItem => {
                                    const newItem = next.list.find(
                                        n => n.report_id === oldItem.report_id
                                    );
                                    if (!newItem) {
                                        return { ...oldItem, cost_water: null };
                                    }
                                    return {
                                        ...oldItem,
                                        ...newItem,
                                        cost_water:
                                            newItem.cost_water !== undefined
                                                ? newItem.cost_water
                                                : null,
                                    };
                                }),
                                applications: next.applications,
                            };
                        }
                        return next;
                    });
                    setLastUpdated(new Date());
                })
                .catch(err => {
                    const { response } = err;
                    setReportPayload(
                        normalizeAggregatedCleaningReportResponse(
                            response ? response.data : undefined
                        )
                    );
                    setLoadError(err);
                })
                .finally(() => {
                    if (!silent) {
                        setReportLoading(false);
                    }
                    setInitialDone(true);
                });
        },
        [cc1Robots, selectedCc1RobotIds]
    );

    const fetchAggregatedReport = useCallback(
        (silent = false, forceRefresh = false) => {
            return fetchAggregatedReportForRange(
                periodDates.startDate,
                periodDates.endDate,
                { silent, forceRefresh }
            );
        },
        [
            fetchAggregatedReportForRange,
            periodDates.startDate,
            periodDates.endDate,
        ]
    );

    useEffect(() => {
        if (cc1RobotsLoading) {
            return undefined;
        }
        fetchAggregatedReport(false);
        if (!autoRefresh) {
            return undefined;
        }
        const intervalId = setInterval(() => {
            fetchAggregatedReport(true);
        }, LIVE_REFRESH_MS);
        return () => clearInterval(intervalId);
    }, [fetchAggregatedReport, autoRefresh, cc1RobotsLoading]);

    useCleaningReportCurrShortcutSync(draftPeriod, setCurrShortcut);

    useEffect(() => {
        if (dataFullscreen) {
            document.body.classList.add(CLEANING_REPORT_DATA_FS_BODY_CLASS);
        } else {
            document.body.classList.remove(CLEANING_REPORT_DATA_FS_BODY_CLASS);
        }
        return () => {
            document.body.classList.remove(CLEANING_REPORT_DATA_FS_BODY_CLASS);
        };
    }, [dataFullscreen]);

    useEffect(() => {
        if (!dataFullscreen) return undefined;
        const onKey = e => {
            if (e.key === 'Escape') {
                setDataFullscreen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [dataFullscreen]);

    const {
        applyPeriod,
        handleCleaningFromDateChange,
        handleCleaningUntilDateChange,
        handleCleaningPeriodShortcut,
    } = useCleaningReportPeriodHandlers(
        draftPeriod,
        setDraftPeriod,
        periodDates,
        setPeriodDates,
        (startYmd, endYmd) => fetchAggregatedReportForRange(startYmd, endYmd),
        [fetchAggregatedReportForRange, periodDates]
    );

    // Gatilho para buscar detalhes reais (água/bateria) de forma assíncrona
    useEffect(() => {
        if (!reportLoading && reportPayload.list.length > 0) {
            const hasMissingDetails = reportPayload.list.some(
                item => item.cost_water === undefined
            );
            if (hasMissingDetails) {
                fetchAggregatedReportForRange(
                    periodDates.startDate,
                    periodDates.endDate,
                    {
                        silent: true,
                        include_details: true,
                    }
                );
            }
        }
    }, [
        reportLoading,
        reportPayload.list,
        fetchAggregatedReportForRange,
        periodDates,
    ]);

    const scrollToCleaningTable = useCallback(
        () => scrollCleaningReportTableIntoView(cleaningTableRef),
        []
    );

    const closeCleaningDetail = useCallback(() => {
        setDetailModalOpen(false);
        setDetailLoading(false);
        setDetailError(null);
        setDetailData(null);
    }, []);

    const openCleaningDetail = useCallback(
        row => {
            if (
                (row && row.report_id) == null ||
                String(row.report_id).trim() === ''
            ) {
                return;
            }
            const robotUid = row._robot && row._robot.id ? row._robot.id : null;
            if (!robotUid) {
                return;
            }
            setDetailModalOpen(true);
            setDetailLoading(true);
            setDetailError(null);
            setDetailData(null);
            const { start_time, end_time } = buildCleaningReportApiTimeRange(
                periodDates.startDate,
                periodDates.endDate
            );
            const rid = encodeURIComponent(String(row.report_id).trim());
            api.get(
                `robots/${encodeURIComponent(
                    robotUid
                )}/cleaning-report/detail/${rid}`,
                {
                    params: { start_time, end_time },
                }
            )
                .then(({ data }) => {
                    if (
                        data &&
                        typeof data === 'object' &&
                        typeof data.code === 'string' &&
                        typeof data.error === 'string'
                    ) {
                        setDetailError(data.error || GENERIC_DETAIL_ERROR);
                        setDetailData(null);
                    } else {
                        setDetailData(data);
                        setDetailError(null);
                    }
                })
                .catch(err => {
                    const { response } = err;
                    const apiMsg = cleaningDetailErrorMessageFromPayload(
                        response ? response.data : undefined
                    );
                    setDetailError(apiMsg || GENERIC_DETAIL_ERROR);
                    setDetailData(null);
                })
                .finally(() => {
                    setDetailLoading(false);
                });
        },
        [periodDates.startDate, periodDates.endDate]
    );

    const {
        graphColor,
        cardColor,
        pieSegmentColors,
    } = getCleaningReportChartColors(colors);

    const lastUpdatedLabel = lastUpdated
        ? lastUpdated.toLocaleString(localeToBcp47(localeCode), {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
          })
        : '—';

    const onCleaningReportTaskRowClick = openCleaningDetail;

    return (
        <PageContent
            title={
                <LocaleMessage msg="page.robots.cleaning_report.pluginspace_title" />
            }
            loading={cc1RobotsLoading || !initialDone || reportLoading}
        >
            <Container
                $fullWidth
                className={dataFullscreen ? classes.fsOuterClamp : undefined}
            >
                {!dataFullscreen && (
                    <CleaningReportPluginspaceLiveShell
                        classes={classes}
                        lastUpdatedLabel={lastUpdatedLabel}
                        reportLoading={reportLoading}
                        onRefresh={() => fetchAggregatedReport(false, false)}
                        onForceFullSync={() =>
                            fetchAggregatedReport(false, true)
                        }
                        autoRefresh={autoRefresh}
                        onAutoRefreshChange={e =>
                            setAutoRefresh(e.target.checked)
                        }
                        onEnterFullscreen={() => setDataFullscreen(true)}
                    />
                )}

                {dataFullscreen && (
                    <Tooltip
                        title={
                            <LocaleMessage msg="page.robots.cleaning_report.live.exit_fullscreen_data" />
                        }
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            className={classes.exitDataFullscreenBtn}
                            startIcon={<MdFullscreenExit size={20} />}
                            onClick={() => setDataFullscreen(false)}
                        >
                            <LocaleMessage msg="page.robots.cleaning_report.live.exit_fullscreen_data" />
                        </Button>
                    </Tooltip>
                )}

                {!dataFullscreen && (loadError || reportPayload.apiError) && (
                    <Typography variant="body2" color="error" className="mb-3">
                        <LocaleMessage msg="page.robots.cleaning_report.fetch_failed" />
                        {reportPayload.apiError && reportPayload.apiError.error
                            ? ` — ${reportPayload.apiError.error}`
                            : ''}
                    </Typography>
                )}

                {!dataFullscreen && (
                    <CleaningReportPluginspaceRobotErrorsAlert
                        robots_errors={robots_errors}
                    />
                )}

                {dataFullscreen ? (
                    <CleaningReportPluginspaceFullscreen
                        classes={classes}
                        loadError={loadError}
                        apiError={reportPayload.apiError}
                        robots_errors={robots_errors}
                        total={total}
                        totalCleanFormatted={totalCleanFormatted}
                        totalArea={totalArea}
                        robotsQueried={robotsQueried}
                        cardColor={cardColor}
                        graphColor={graphColor}
                        pieSegmentColors={pieSegmentColors}
                        list={list}
                        localeCode={localeCode}
                        periodDates={periodDates}
                        fsAreaChartHeight={fsAreaChartHeight}
                        scrollToCleaningTable={scrollToCleaningTable}
                        cleaningTableRef={cleaningTableRef}
                        tableRowsForDisplay={tableRowsForDisplay}
                        onRowClick={onCleaningReportTaskRowClick}
                        tasksTablePage={tasksTablePage}
                        tasksTableRowsPerPage={tasksTableRowsPerPage}
                        tasksTableTotalCount={list.length}
                        onTasksTablePageChange={handleTasksTablePageChange}
                        onTasksTableRowsPerPageChange={
                            handleTasksTableRowsPerPageChange
                        }
                        reportPayload={reportPayload}
                    />
                ) : null}

                {!dataFullscreen && (
                    <CleaningReportPluginspaceMainCard
                        cc1Robots={cc1Robots}
                        cc1RobotsLoading={cc1RobotsLoading}
                        selectedCc1RobotIds={draftSelectedCc1RobotIds}
                        onCc1RobotFilterChange={handleCc1RobotFilterChange}
                        onCc1RobotFilterConfirm={handleCc1RobotFilterConfirm}
                        date_loc={date_loc}
                        draftPeriod={draftPeriod}
                        handleCleaningFromDateChange={
                            handleCleaningFromDateChange
                        }
                        handleCleaningUntilDateChange={
                            handleCleaningUntilDateChange
                        }
                        applyPeriod={applyPeriod}
                        currShortcut={currShortcut}
                        shortcut_list={shortcut_list}
                        handleCleaningPeriodShortcut={
                            handleCleaningPeriodShortcut
                        }
                        total={total}
                        totalCleanFormatted={totalCleanFormatted}
                        totalArea={totalArea}
                        robotsQueried={robotsQueried}
                        cardColor={cardColor}
                        graphColor={graphColor}
                        pieSegmentColors={pieSegmentColors}
                        list={list}
                        localeCode={localeCode}
                        periodDates={periodDates}
                        areaCompareSeries={areaCompareSeries}
                        areaByHourSeries={areaByHourSeries}
                        scrollToCleaningTable={scrollToCleaningTable}
                        cleaningTableRef={cleaningTableRef}
                        tableRowsForDisplay={tableRowsForDisplay}
                        onRowClick={onCleaningReportTaskRowClick}
                        tasksTablePage={tasksTablePage}
                        tasksTableRowsPerPage={tasksTableRowsPerPage}
                        tasksTableTotalCount={list.length}
                        onTasksTablePageChange={handleTasksTablePageChange}
                        onTasksTableRowsPerPageChange={
                            handleTasksTableRowsPerPageChange
                        }
                        humanProd={fleetSettings.human_productivity}
                        dischargeRate={fleetSettings.discharge_rate}
                        tankCapacity={fleetSettings.tank_capacity}
                        manualReference={fleetSettings.manual_reference}
                        chemicalDilutionRatio={
                            fleetSettings.chemical_dilution_ratio
                        }
                        reportPayload={reportPayload}
                        loadError={loadError}
                    />
                )}

                <CleaningReportDetailModal
                    open={detailModalOpen}
                    onClose={closeCleaningDetail}
                    loading={detailLoading}
                    errorMessage={detailError}
                    detail={detailData}
                    localeCode={localeCode}
                    chemicalDilutionRatio={
                        fleetSettings.chemical_dilution_ratio
                    }
                />
            </Container>
        </PageContent>
    );
}
